import PromoDisplayModel from './PromoDisplayModel'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import useClickOutside from 'hooks/useClickOutside'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { matchPath } from 'react-router'
import useCopyToClipboard from 'hooks/useCopyToClipboard'
import { checkPromoDisplayConfig, removePromoDisplay } from 'actions/MarketingAction'
import { getCookies, setCookie } from 'lib/web/cookieUtils'
import { getCurrentUserId, selectLoggedIn } from 'selectors/accountSelectors'
import * as Analytics from 'analytics/analytics'
import { promotionClickEvent, promotionViewEvent } from 'analytics/snowplow/events'
import AccountAccessPromptIcon from 'components/Account/AccountAccessPrompt/AccountAccessPromptIcon'
import valentineTakeoverConfig from 'takeovers/valentineTakeover/valentineTakeoverConfig'
import Image from 'components/Common/Image'
import useCurrentSiteTakeover from 'takeovers/utils/hooks/useCurrentSiteTakeover'

const PROMO_DISPLAY_COOKIE_NAME = 'promoDisplay'
// To ensure we delay the promo display by at least 1 second
const PROMO_DISPLAY_DELAY_MOFIDIER = 1000
const UNKNOWN_PROMO_CODE = 'UNKNOWN_PROMO_CODE'

const getOfferId = (state: App.State): string | undefined => {
  if (!state.routeHistory.currentPathParams) return
  let offerId = (state.routeHistory.currentPathParams as any).offerId
  // For some dum reason the cruises analytics page params has id and not offerId
  if (!offerId) offerId = (state.routeHistory.currentPathParams as any).id
  return offerId
}

const getPromoDisplayConfig = (pathname: string, promoDisplayConfig: App.PromoDisplayConfigMap | undefined, region: string, overridePromoDisplayConfig: App.PromoDisplayConfig | undefined) => {
  if (overridePromoDisplayConfig) {
    return overridePromoDisplayConfig
  }

  if (!promoDisplayConfig) {
    return
  }
  return Object.values(promoDisplayConfig)?.find((config) => !!matchPath(pathname, { path: `/${region}${config.urlPath}`, exact: true }))
}

interface Props {
  overridePromoDisplayPrompt?: App.PromoDisplayConfig
}

function PromoDisplayPrompt({ overridePromoDisplayPrompt }: Props) {
  const promptRef = useRef<HTMLDivElement>(null)
  const currentPath = useAppSelector(state => state.router.location.pathname)
  const userId = useAppSelector(state => getCurrentUserId(state))
  const domainUserId = useAppSelector(state => state.auth.domainUserId)
  const currentRegion = useAppSelector(state => state.geo.currentRegionCode)
  const isLoggedIn = useAppSelector(state => selectLoggedIn(state))
  const storedPromoDisplayConfig = useAppSelector(state => state.marketing.promoDisplayConfigs)
  const [promoDisplayConfig, setPromoDisplayConfig] = useState<App.PromoDisplayConfig | undefined>(getPromoDisplayConfig(currentPath, storedPromoDisplayConfig, currentRegion, overridePromoDisplayPrompt))
  const [show, setShow] = useState<boolean>(false)
  const [, copy] = useCopyToClipboard()
  const [isCopied, setIsCopied] = useState(false)
  const dispatch = useAppDispatch()
  const offerId = useAppSelector(state => getOfferId(state))
  const passAuthStateCheck = useMemo(() => {
    if (promoDisplayConfig?.authState === 'logged_in_and_out') {
      return true
    }
    if (promoDisplayConfig?.authState === 'logged_in' && isLoggedIn) {
      return true
    }
    if (promoDisplayConfig?.authState === 'logged_out' && !isLoggedIn) {
      return true
    }
    return false
  }, [promoDisplayConfig, isLoggedIn])

  const isValentineTakeover = useCurrentSiteTakeover()?.id === valentineTakeoverConfig.id

  const HeaderIcon = useMemo(() => {
    const codeName = promoDisplayConfig?.codeName
    if (codeName === 'LOVEISINTHEAIR' && isValentineTakeover) {
      return <Image width={90} id="ravf7iouyaoeqloomvue" enhancedFormats={false} />
    } else {
      return <AccountAccessPromptIcon />
    } }, [promoDisplayConfig, isValentineTakeover])

  const setPromoDisplayCookie = useCallback((promoDisplayId: string) => {
    const allCookies = getCookies()
    const promoDisplayCookie = allCookies[PROMO_DISPLAY_COOKIE_NAME]
    setCookie(PROMO_DISPLAY_COOKIE_NAME, `${promoDisplayCookie ? promoDisplayCookie + ',' : ''}${promoDisplayId}`, 30 * 24 * 60 * 60, '/', 'Lax')
  }, [])

  useEffect(() => {
    const newPromoDisplayConfig = getPromoDisplayConfig(currentPath, storedPromoDisplayConfig, currentRegion, overridePromoDisplayPrompt)
    // If the promo needs an offer check and the offer check fails, we should not show the promo
    const offerCheckFailed = newPromoDisplayConfig?.needsOfferCheck && (!offerId || (currentPath in newPromoDisplayConfig.passOfferCheck && !newPromoDisplayConfig.passOfferCheck[currentPath]?.checkPassed))
    if (offerCheckFailed) {
      setPromoDisplayConfig(undefined)
    } else {
      setPromoDisplayConfig(newPromoDisplayConfig)

      // Check if we need to perform additional offer validation
      if (newPromoDisplayConfig?.needsOfferCheck && offerId && !(currentPath in (newPromoDisplayConfig.passOfferCheck ?? {}))) {
        const userIdentifier = isLoggedIn && userId ? { userId: userId } : { sessionId: domainUserId }
        const pathBase64 = btoa(currentPath)
        dispatch(checkPromoDisplayConfig({
          promoDisplayId: newPromoDisplayConfig.promoDisplayId, offerId, path: currentPath, query: { ...userIdentifier, sitePathBase64: pathBase64, region: currentRegion },
        }))
      }
    }
  }, [currentPath, currentRegion, storedPromoDisplayConfig, isLoggedIn, overridePromoDisplayPrompt, offerId, dispatch, domainUserId, userId])

  // Handle display logic and timing
  useEffect(() => {
    if (!promoDisplayConfig) {
      return
    }
    let shouldShow = !!promoDisplayConfig
    if (shouldShow && promoDisplayConfig.needsOfferCheck) {
      shouldShow = !!promoDisplayConfig.passOfferCheck?.[currentPath]?.checkPassed
    }
    const timer = setTimeout(() => setShow(shouldShow), promoDisplayConfig.delayDuration * PROMO_DISPLAY_DELAY_MOFIDIER)
    return () => clearTimeout(timer)
  }, [promoDisplayConfig, dispatch, isLoggedIn, setShow, currentPath, userId])

  // Handle copy state timeout
  useEffect(() => {
    if (isCopied) {
      const timer = setTimeout(() => {
        setIsCopied(false)
      }, 3000)
      return () => clearTimeout(timer)
    }
  }, [isCopied])

  // Track analytics when promo is shown
  useEffect(() => {
    if (show) {
      const codeName = promoDisplayConfig?.codeName
      Analytics.trackEvent(promotionViewEvent({
        promotionContext: { promotionName: 'promo_display', promotionType: 'prompt' },
        promotionCodeContext: { codeName: codeName ?? UNKNOWN_PROMO_CODE, displayOptionKey: promoDisplayConfig?.displayOptionKey ?? '', promoDisplayId: promoDisplayConfig?.promoDisplayId ?? '' },
      }))
    }
  }, [show, promoDisplayConfig])

  const onClickCopyIcon = useCallback(() => {
    const codeName = promoDisplayConfig?.codeName
    copy(codeName ?? '')
    setIsCopied(true)
    Analytics.trackEvent(promotionClickEvent({
      promotionContext: { promotionName: 'promo_display', promotionType: 'prompt' },
      promotionCodeContext: { codeName: codeName ?? UNKNOWN_PROMO_CODE, displayOptionKey: promoDisplayConfig?.displayOptionKey ?? '', promoDisplayId: promoDisplayConfig?.promoDisplayId ?? '' },
    }))
  }, [promoDisplayConfig, copy])

  const dismissPrompt = useCallback(() => {
    setShow(false)
  }, [])

  useClickOutside(promptRef, dismissPrompt, show)
  const onClose = useCallback(() => {
    if (promoDisplayConfig?.displayMode === 'appear_once') {
      setPromoDisplayCookie(promoDisplayConfig.promoDisplayId)
      sessionStorage.setItem('promoDisplayId', promoDisplayConfig.promoDisplayId)
      dispatch(removePromoDisplay(promoDisplayConfig.promoDisplayId))
      setPromoDisplayConfig(undefined)
    }
    dismissPrompt()
  }, [promoDisplayConfig, dismissPrompt, dispatch, setPromoDisplayCookie])

  if (!passAuthStateCheck) {
    return null
  }

  return (
    <PromoDisplayModel
      show={show}
      promptRef={promptRef}
      HeaderIcon={HeaderIcon}
      codeName={promoDisplayConfig?.codeName}
      expiresAt={promoDisplayConfig?.expiresAt}
      bodyText={promoDisplayConfig?.bodyText}
      headerText={promoDisplayConfig?.headerText}
      displayMode={promoDisplayConfig?.displayMode}
      isCopied={isCopied}
      onClose={onClose}
      onClickCopyIcon={onClickCopyIcon}
    />
  )
}

export default React.memo(PromoDisplayPrompt)
